import { useRef, useMemo, useState, useEffect } from 'react'

import { useResponsive } from 'hooks/useResponsive'

import { MOBILE_SIZE_BREAKPOINT } from 'consts'

import debounce from 'lodash.debounce'

import * as Styled from './styled'
import { IBaseDropdownItem, IDropdownHeaderProps } from '../types'

export const DropdownHeader = <T extends IBaseDropdownItem>({
  isOpen,
  activeItem,
  onSearch,
  ChildComponent,
  hasContent
}: IDropdownHeaderProps<T>) => {
  const isTablet = useResponsive(MOBILE_SIZE_BREAKPOINT)
  const contentRef = useRef<HTMLInputElement>(null)
  const [isEditing, setIsEditing] = useState<boolean>(isOpen)
  const [searchQuery, setSearchQuery] = useState<string>('')

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        onSearch?.(value)
      }, 600),
    [onSearch]
  )

  const handleContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchQuery(value)
    debouncedSearch(value.trim())
  }

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  useEffect(() => {
    if (isTablet) {
      return
    }
    if (isOpen && hasContent) {
      setIsEditing(true)
      setSearchQuery('')
    } else {
      setIsEditing(false)
      onSearch?.('')
    }
  }, [isOpen, onSearch, hasContent, isTablet])

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const currentActiveItem = useMemo(() => {
    return <ChildComponent item={activeItem} isActive isHeader />
  }, [activeItem, ChildComponent])

  return (
    <Styled.DropdownHeaderWrapper>
      {isEditing ? (
        <Styled.SearchInputWrapper>
          <Styled.SearchInput
            ref={contentRef}
            value={searchQuery}
            onChange={handleContentChange}
            onClick={handleInputClick}
            maxLength={20}
            autoFocus
          />
        </Styled.SearchInputWrapper>
      ) : (
        currentActiveItem
      )}
    </Styled.DropdownHeaderWrapper>
  )
}
